import { useEffect, useState } from 'react'
import {
  listShifts,
  createShift,
  updateShift,
  deleteShift,
  addShiftTask,
  deleteShiftTask,
  listClients,
  listUsers,
} from '../../lib/api'

const CARD = { background: '#FAF7F0', border: '1px solid #D0C9B7', borderRadius: 8, padding: 'clamp(18px, 4vw, 26px)' }
const INPUT = {
  fontFamily: 'Inter, Arial, sans-serif', fontSize: 16, color: '#191917', background: '#FFFFFF',
  border: '1px solid #D0C9B7', borderRadius: 4, padding: '12px 14px',
  width: '100%', maxWidth: '100%', boxSizing: 'border-box', minHeight: 48,
}
const LABEL = { display: 'block', fontSize: 14, fontWeight: 600, color: '#003F3A', marginBottom: 6 }
const BTN = {
  background: '#00564F', color: '#FAF7F0', border: '1px solid #00564F', borderRadius: 4,
  padding: '12px 18px', minHeight: 48, fontWeight: 600, fontSize: 15,
  fontFamily: 'Inter, Arial, sans-serif', cursor: 'pointer',
}
const BTN_GHOST = { ...BTN, background: 'transparent', color: '#00564F' }
const SMALL = { fontSize: 13, color: '#5D645B' }

const fmtDay = (d) => {
  try {
    return new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-CA', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    })
  } catch {
    return d
  }
}

const statusOf = (s) => {
  if (s.checked_out_at) return { label: 'Completed', bg: '#B7C4B7', fg: '#003F3A' }
  if (s.checked_in_at) return { label: 'In progress', bg: '#00564F', fg: '#FAF7F0' }
  if (!s.caregiver_id) return { label: 'Unassigned', bg: '#967057', fg: '#FFFFFF' }
  return { label: 'Scheduled', bg: '#EEE8DA', fg: '#003F3A' }
}

const EMPTY = { clientId: '', caregiverId: '', shiftDate: '', startTime: '', endTime: '', tasks: '' }

export default function ScheduleTab() {
  const [shifts, setShifts] = useState([])
  const [clients, setClients] = useState([])
  const [caregivers, setCaregivers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [taskDrafts, setTaskDrafts] = useState({})

  const load = async () => {
    try {
      const [s, c, u] = await Promise.all([listShifts(), listClients(), listUsers()])
      setShifts(s.shifts)
      setClients(c.clients)
      setCaregivers(u.users.filter((x) => x.role === 'caregiver' && x.is_active))
      setError('')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const guard = async (fn) => {
    if (busy) return
    setBusy(true)
    try {
      await fn()
      setError('')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const addVisit = (e) => {
    e.preventDefault()
    return guard(async () => {
      await createShift({
        clientId: form.clientId,
        caregiverId: form.caregiverId || null,
        shiftDate: form.shiftDate,
        startTime: form.startTime,
        endTime: form.endTime,
        tasks: form.tasks.split('\n').map((t) => t.trim()).filter(Boolean),
      })
      setForm(EMPTY)
      await load()
    })
  }

  const reassign = (id, caregiverId) =>
    guard(async () => {
      await updateShift(id, { caregiverId: caregiverId || null })
      await load()
    })

  const addTask = (id) =>
    guard(async () => {
      const label = (taskDrafts[id] || '').trim()
      if (!label) return
      const { task } = await addShiftTask(id, label)
      setShifts((list) => list.map((s) => (s.id === id ? { ...s, tasks: [...(s.tasks || []), task] } : s)))
      setTaskDrafts((d) => ({ ...d, [id]: '' }))
    })

  const removeTask = (id, taskId) =>
    guard(async () => {
      await deleteShiftTask(id, taskId)
      setShifts((list) =>
        list.map((s) => (s.id === id ? { ...s, tasks: (s.tasks || []).filter((t) => t.id !== taskId) } : s))
      )
    })

  const removeVisit = (id, who) =>
    guard(async () => {
      if (!window.confirm(`Delete this visit for ${who}? The caregiver will no longer see it.`)) return
      await deleteShift(id)
      setShifts((list) => list.filter((s) => s.id !== id))
    })

  if (loading) return <p style={{ color: '#5D645B' }}>Loading the schedule…</p>

  const noClients = clients.length === 0
  const noCaregivers = caregivers.length === 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {error && (
        <div style={{ ...CARD, background: '#FBECEC', borderColor: '#E4B9B9', color: '#7A2C2C' }}>{error}</div>
      )}

      {(noClients || noCaregivers) && (
        <div style={{ ...CARD, background: '#F6F1E4' }}>
          <p style={{ margin: 0, fontSize: 15, color: '#5D645B' }}>
            {noClients && 'Add a client in Users & Access before scheduling a visit. '}
            {noCaregivers && 'No active caregiver accounts yet — add them in Users & Access so you can assign visits.'}
          </p>
        </div>
      )}

      {/* ---- schedule a visit ---- */}
      <div style={CARD}>
        <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 26, fontWeight: 600, color: '#003F3A', margin: '0 0 4px 0' }}>
          Schedule a visit
        </h2>
        <p style={{ ...SMALL, margin: '0 0 20px 0' }}>
          Pick the client, choose which caregiver attends, and list what needs doing. The caregiver ticks the list off
          during the visit and the family sees it afterwards.
        </p>

        <form onSubmit={addVisit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="grid grid-2" style={{ gap: 16 }}>
            <div style={{ minWidth: 0 }}>
              <label style={LABEL} htmlFor="sch-client">Client receiving care *</label>
              <select
                id="sch-client" style={INPUT} required value={form.clientId}
                onChange={(e) => setForm({ ...form, clientId: e.target.value })}
              >
                <option value="">Choose a client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={LABEL} htmlFor="sch-caregiver">Assigned caregiver</label>
              <select
                id="sch-caregiver" style={INPUT} value={form.caregiverId}
                onChange={(e) => setForm({ ...form, caregiverId: e.target.value })}
              >
                <option value="">Leave unassigned for now</option>
                {caregivers.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-3" style={{ gap: 16 }}>
            <div style={{ minWidth: 0 }}>
              <label style={LABEL} htmlFor="sch-date">Date *</label>
              <input
                id="sch-date" style={INPUT} type="date" required value={form.shiftDate}
                onChange={(e) => setForm({ ...form, shiftDate: e.target.value })}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={LABEL} htmlFor="sch-start">Start time</label>
              <input
                id="sch-start" style={INPUT} placeholder="9:00 AM" value={form.startTime}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={LABEL} htmlFor="sch-end">End time</label>
              <input
                id="sch-end" style={INPUT} placeholder="12:00 PM" value={form.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label style={LABEL} htmlFor="sch-tasks">Task checklist <span style={{ fontWeight: 400, color: '#5D645B' }}>— one task per line</span></label>
            <textarea
              id="sch-tasks" style={{ ...INPUT, minHeight: 120 }} value={form.tasks}
              placeholder={'Morning routine support\nPrepare breakfast and lunch\nLight housekeeping — kitchen\nMedication reminder (10 AM)'}
              onChange={(e) => setForm({ ...form, tasks: e.target.value })}
            />
          </div>

          <button type="submit" style={{ ...BTN, alignSelf: 'flex-start' }} disabled={busy || noClients}>
            {busy ? 'Saving…' : 'Schedule this visit'}
          </button>
        </form>
      </div>

      {/* ---- existing visits ---- */}
      {shifts.length === 0 ? (
        <p style={{ ...SMALL, margin: 0 }}>No visits scheduled yet.</p>
      ) : (
        shifts.map((s) => {
          const st = statusOf(s)
          const tasks = s.tasks || []
          const doneCount = tasks.filter((t) => t.done).length
          return (
            <div key={s.id} style={CARD}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, fontWeight: 600, color: '#003F3A' }}>
                    {fmtDay(s.shift_date)}
                    {(s.start_time || s.end_time) && ` · ${[s.start_time, s.end_time].filter(Boolean).join(' – ')}`}
                  </div>
                  <div style={SMALL}>
                    Care for <strong style={{ color: '#191917' }}>{s.client_name}</strong>
                    {s.checked_in_at && ` · checked in ${new Date(s.checked_in_at).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' })}`}
                    {s.checked_out_at && ` · out ${new Date(s.checked_out_at).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' })}`}
                  </div>
                </div>
                <span style={{ background: st.bg, color: st.fg, borderRadius: 4, padding: '6px 12px', fontSize: 14, fontWeight: 600, height: 'fit-content' }}>
                  {st.label}
                </span>
              </div>

              <div className="grid grid-2" style={{ gap: 16, marginBottom: 16 }}>
                <div style={{ minWidth: 0 }}>
                  <label style={LABEL} htmlFor={`cg-${s.id}`}>Assigned caregiver</label>
                  <select
                    id={`cg-${s.id}`} style={INPUT} value={s.caregiver_id || ''}
                    onChange={(e) => reassign(s.id, e.target.value)} disabled={busy}
                  >
                    <option value="">Unassigned</option>
                    {caregivers.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                  </select>
                </div>
                <div style={{ minWidth: 0, display: 'flex', alignItems: 'flex-end' }}>
                  <button type="button" style={{ ...BTN_GHOST, borderColor: '#B0433C', color: '#B0433C' }}
                    onClick={() => removeVisit(s.id, s.client_name)} disabled={busy}>
                    Delete visit
                  </button>
                </div>
              </div>

              <label style={LABEL}>
                Task checklist {tasks.length > 0 && <span style={{ fontWeight: 400, color: '#5D645B' }}>— {doneCount} of {tasks.length} done</span>}
              </label>
              {tasks.length === 0 && <p style={{ ...SMALL, margin: '0 0 10px 0' }}>No tasks yet — add the first one below.</p>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                {tasks.map((t) => (
                  <div key={t.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, background: '#FFFFFF',
                    border: '1px solid #D0C9B7', borderRadius: 4, padding: '10px 12px', minHeight: 48, boxSizing: 'border-box',
                  }}>
                    <span style={{ fontWeight: 700, color: t.done ? '#6B8B75' : '#D0C9B7' }}>{t.done ? '✓' : '○'}</span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 15, color: t.done ? '#191917' : '#5D645B', overflowWrap: 'anywhere' }}>
                      {t.label}
                    </span>
                    <button
                      type="button" onClick={() => removeTask(s.id, t.id)} disabled={busy}
                      aria-label={`Remove task: ${t.label}`}
                      style={{ background: 'transparent', border: 'none', color: '#B0433C', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '8px 4px' }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              <div className="grid" style={{ '--cols': 'minmax(0, 1fr) auto', gap: 10 }}>
                <input
                  style={INPUT} placeholder="Add a task…" value={taskDrafts[s.id] || ''}
                  onChange={(e) => setTaskDrafts((d) => ({ ...d, [s.id]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTask(s.id) } }}
                />
                <button type="button" style={BTN_GHOST} onClick={() => addTask(s.id)} disabled={busy}>Add task</button>
              </div>

              {s.note && (
                <div style={{ marginTop: 14, background: '#EEE8DA', borderRadius: 6, padding: '14px 16px', fontSize: 15 }}>
                  <strong>Caregiver note:</strong> {s.note}
                </div>
              )}
            </div>
          )
        })
      )}
    </div>
  )
}
